import React, { useState, useEffect } from 'react';
import { 
  PlayIcon, 
  ClockIcon, 
  BriefcaseIcon, 
  VideoCameraIcon, 
  MicrophoneIcon, 
  PencilIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setMediaPermissionGranted,
  selectSessionData,
  selectLoading,
  selectInterviewType
} from '@/store/slices/interviewSlice';

const TERMS_VERSION = 'v1.0.0';

const InterviewStart = ({ onStart }) => {
  const dispatch = useDispatch();
  
  // Redux selectors
  const sessionData = useSelector(selectSessionData);
  const loading = useSelector(selectLoading);
  const allowedFormats = useSelector(selectInterviewType); // ['video', 'audio', 'text']
  
  // Check if this is a resumed interview
  const isResumed = !!(
    sessionData &&
    sessionData.progress &&
    sessionData.progress.is_started === true &&
    sessionData.progress.is_completed === false
  );
  
  // Local state - auto-select video since it's the only option
  const [selectedFormat, setSelectedFormat] = useState('video');
  const [termsAccepted, setTermsAccepted] = useState(isResumed); // Auto-accept if resumed
  const [recordingConsent, setRecordingConsent] = useState(isResumed); // Auto-accept if resumed
  const [mediaPermissionGranted, setMediaPermissionGrantedLocal] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [permissionRequested, setPermissionRequested] = useState(false);
  const [showLegalText, setShowLegalText] = useState(false);

  const [mediaError, setMediaError] = useState(null);

  // Auto-proceed for resumed interviews once media is ready
  useEffect(() => {
    if (
      isResumed &&
      !loading &&
      selectedFormat === 'video' &&
      mediaPermissionGranted &&
      termsAccepted &&
      recordingConsent
    ) {
      // Auto-call handleStart after a brief delay to ensure all state is ready
      const timer = setTimeout(() => {
        dispatch(setMediaPermissionGranted(mediaPermissionGranted));
        onStart({
          termsAccepted: true,
          termsVersion: TERMS_VERSION,
          recordingConsent: true,
          mediaMode: selectedFormat,
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isResumed, loading, selectedFormat, mediaPermissionGranted, termsAccepted, recordingConsent, dispatch, onStart]);

  // Request media permission for video (always since video is only format)
  useEffect(() => {
    const requestMediaPermission = async () => {
      if (selectedFormat === 'video' && !permissionRequested) {
        setPermissionRequested(true);
        setMediaError(null);
        
        try {
          const constraints = {
            video: selectedFormat === 'video',
            audio: true
          };
          
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          setMediaStream(stream);
          setMediaPermissionGrantedLocal(true);
        } catch (error) {
          console.error('Media permission error:', error);
          setMediaPermissionGrantedLocal(false);
          
          // User-friendly error messages
          let errorMessage = 'Unable to access media devices.';
          
          if (error.name === 'NotFoundError') {
            errorMessage = selectedFormat === 'video' 
              ? 'No camera found. Please connect a camera or switch to Audio/Text mode.'
              : 'No microphone found. Please connect a microphone or switch to Text mode.';
          } else if (error.name === 'NotAllowedError') {
            errorMessage = 'Permission denied. Please allow access in your browser settings.';
          } else if (error.name === 'NotReadableError') {
            errorMessage = 'Device is already in use by another application.';
          } else if (error.name === 'OverconstrainedError') {
            errorMessage = 'Device does not meet requirements. Try a different device.';
          }
          
          setMediaError(errorMessage);
        }
      } else if (selectedFormat === 'text') {
        if (mediaStream) {
          mediaStream.getTracks().forEach(track => track.stop());
          setMediaStream(null);
        }
        setMediaPermissionGrantedLocal(true);
        setPermissionRequested(false);
        setMediaError(null);
      } else if (selectedFormat === null) {
        if (mediaStream) {
          mediaStream.getTracks().forEach(track => track.stop());
          setMediaStream(null);
        }
        setMediaPermissionGrantedLocal(false);
        setPermissionRequested(false);
        setMediaError(null);
      }
    };

    requestMediaPermission();
  }, [selectedFormat, permissionRequested]);

  // Cleanup media stream on unmount
  useEffect(() => {
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mediaStream]);

  if (!sessionData) return null;

  const answerFormats = [
    {
      id: 'video',
      name: 'Video Recording',
      description: 'Record your responses with camera and audio',
      icon: VideoCameraIcon,
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      highlight: 'purple-500'
    },
    {
      id: 'audio',
      name: 'Audio Recording',
      description: 'Record voice responses that get transcribed',
      icon: MicrophoneIcon,
      color: 'green',
      gradient: 'from-green-500 to-green-600',
      bgGradient: 'from-green-50 to-green-100',
      highlight: 'green-500'
    },
    {
      id: 'text',
      name: 'Text Only',
      description: 'Type your answers using the keyboard',
      icon: PencilIcon,
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      highlight: 'blue-500'
    }
  ].filter(format => allowedFormats.includes(format.id)); ;

  const handleStart = () => {
    if (termsAccepted && recordingConsent) {
      dispatch(setMediaPermissionGranted(mediaPermissionGranted));
      
      onStart({
        termsAccepted,
        termsVersion: TERMS_VERSION,
        recordingConsent,
        mediaMode: selectedFormat,
      });
    }
  };

  // Hide component only if interview is completed (not just started)
  const isInterviewCompleted = !!(
    sessionData &&
    sessionData.progress &&
    sessionData.progress.is_completed === true
  );

  if (isInterviewCompleted) {
    return null;
  }

  // Show resuming message for resumed interviews while auto-proceeding
  if (isResumed && mediaPermissionGranted && termsAccepted && recordingConsent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50 -mt-8 flex items-center justify-center">
        <div className="relative max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-1 bg-white rounded-full"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayIcon className="w-8 h-8 text-indigo-600" />
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Resuming Interview</h2>
            <p className="text-gray-600">Preparing to continue your interview...</p>
          </div>
        </div>
      </div>
    );
  }

  const canStart = termsAccepted && 
                   recordingConsent && 
                   selectedFormat &&
                   ((selectedFormat === 'text') || 
                    (selectedFormat === 'video' || selectedFormat === 'audio') && mediaPermissionGranted);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50 -mt-8">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-4">
            <SparklesIcon className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-semibold text-indigo-600 tracking-wide">
              AI-POWERED TRANSCRIPTION
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Welcome to Your Interview
          </h1>
          
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Ready to showcase your skills for{' '}
            <span className="font-semibold text-gray-900">
              {sessionData.job_title || 'this position'}
            </span>
          </p>

          {/* Quick info badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
              <ClockIcon className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-gray-700">
                15-20 min
              </span>
            </div>
            
            {sessionData.company_name && (
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                <BriefcaseIcon className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">
                  {sessionData.company_name}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Format Selection */}
        <div className="mb-8 sm:mb-10">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm mr-3">1</span>
              Ready to Record
            </h2>
            <p className="text-gray-600 mb-6 ml-11">
              The interview will be conducted via video. Your responses will be recorded and securely saved.
            </p>
            
            <div className="hidden">
              {answerFormats.map((format) => {
                const IconComponent = format.icon;
                const isSelected = selectedFormat === format.id;
                
                return (
                  <button
                    key={format.id}
                    onClick={() => {
                      setSelectedFormat(format.id);
                      setMediaPermissionGrantedLocal(false);
                      setPermissionRequested(false);
                      if (mediaStream) {
                        mediaStream.getTracks().forEach(track => track.stop());
                        setMediaStream(null);
                      }
                    }}
                    className={`relative group text-left p-5 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 ${
                      isSelected
                        ? `border-${format.highlight} bg-gradient-to-br ${format.bgGradient} shadow-lg`
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className={`absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br ${format.gradient} rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform`}>
                        <CheckCircleIcon className="w-5 h-5 text-white" />
                      </div>
                    )}

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                      isSelected ? `bg-white/50` : 'bg-gray-100'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${
                        isSelected ? `text-${format.color}-600` : 'text-gray-500'
                      }`} />
                    </div>

                    {/* Content */}
                    <h3 className={`font-bold text-base mb-1 ${
                      isSelected ? 'text-gray-900' : 'text-gray-800'
                    }`}>
                      {format.name}
                    </h3>
                    <p className={`text-sm ${
                      isSelected ? 'text-gray-700' : 'text-gray-600'
                    }`}>
                      {format.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Media Permission Check - Video Only */}
            {selectedFormat === 'video' && (
              <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200">
                <div className="flex items-start space-x-4">
                  {/* Camera Preview */}
                  <div className="flex-1">
                    {selectedFormat === 'video' && (
                      <div className="relative">
                        {mediaError ? (
                          <div className="aspect-video bg-red-50 rounded-lg flex items-center justify-center border-2 border-red-200">
                            <div className="text-center px-4">
                              <VideoCameraIcon className="w-12 h-12 text-red-400 mx-auto mb-3" />
                              <p className="text-sm font-medium text-red-900 mb-2">Device Error</p>
                              <p className="text-xs text-red-700">{mediaError}</p>
                              <button
                                onClick={() => {
                                  setPermissionRequested(false);
                                  setMediaError(null);
                                }}
                                className="mt-3 text-xs text-red-600 hover:text-red-800 underline"
                              >
                                Try Again
                              </button>
                            </div>
                          </div>
                        ) : mediaStream ? (
                          <div className="relative rounded-lg overflow-hidden bg-gray-900">
                            <video
                              ref={videoRef => {
                                if (videoRef && mediaStream) {
                                  videoRef.srcObject = mediaStream;
                                  videoRef.play();
                                }
                              }}
                              className="w-full rounded-lg aspect-video"
                              muted
                              playsInline
                            />
                            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center space-x-1">
                              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                              <span>LIVE</span>
                            </div>
                          </div>
                        ) : (
                          <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                            <div className="text-center">
                              <VideoCameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">
                                {permissionRequested ? 'Requesting permission...' : 'Waiting for permission'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0 w-48 hidden sm:block">
                    <div className="space-y-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Camera Status</div>
                      
                      {mediaError ? (
                        <div className="flex items-start space-x-2">
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-red-600">Error</div>
                            <div className="text-xs text-gray-600">Check device</div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start space-x-2">
                            {mediaPermissionGranted ? (
                              <>
                                <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">Permission Granted</div>
                                  <div className="text-xs text-gray-600">Camera & mic active</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mt-0.5"></div>
                                <div>
                                  <div className="text-sm font-medium text-orange-600">Waiting</div>
                                  <div className="text-xs text-gray-600">Allow access in browser</div>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Tips */}
        <div className="mb-8 sm:mb-10">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm mr-3">2</span>
              Before You Begin
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3 p-4 bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-100">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Quiet Environment</div>
                  <div className="text-sm text-gray-600">Find a distraction-free space</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-gradient-to-br from-sky-50 to-white rounded-lg border border-sky-100">
                <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Stable Connection</div>
                  <div className="text-sm text-gray-600">Ensure good internet speed</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Take Your Time</div>
                  <div className="text-sm text-gray-600">Think before answering</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-100">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Be Yourself</div>
                  <div className="text-sm text-gray-600">Authentic responses matter</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consent Section */}
        <div className="mb-8 sm:mb-10">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold text-sm mr-3">3</span>
              Terms & Privacy
            </h2>

            {/* Privacy assurance banner */}
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
              <div className="flex items-start space-x-3">
                <ShieldCheckIcon className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900 mb-1">Your Privacy is Protected</div>
                  <div className="text-sm text-emerald-800">
                    We use AI only to transcribe your speech to text. Your video is reviewed by humans only - 
                    no AI analyzes your body language, tone, or expressions.
                  </div>
                </div>
              </div>
            </div>

            {/* Consent checkboxes */}
            <div className="space-y-4">
              {/* Terms acceptance */}
              <label className="flex items-start space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-indigo-300 cursor-pointer transition-colors group">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 group-hover:text-indigo-700 transition-colors">
                    I accept the Terms & Conditions
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Read our{' '}
                    <a 
                      href="https://teamsyncai.com/legal/terms" 
                      className="text-indigo-600 hover:text-indigo-700 underline font-medium"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                  </div>
                </div>
              </label>

              {/* Recording consent */}
              <label className="flex items-start space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-indigo-300 cursor-pointer transition-colors group">
                <div className="flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={recordingConsent}
                    onChange={e => setRecordingConsent(e.target.checked)}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 group-hover:text-indigo-700 transition-colors">
                    I consent to audio/video recording
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Review our{' '}
                    <a 
                      href="https://teamsyncai.com/legal/recording-consent" 
                      className="text-indigo-600 hover:text-indigo-700 underline font-medium"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Recording Consent Policy
                    </a>
                  </div>
                </div>
              </label>
            </div>

            {/* Legal links toggle */}
            <div className="mt-4">
              <button
                onClick={() => setShowLegalText(!showLegalText)}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {showLegalText ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
                <span>Additional Legal Information</span>
              </button>
              
              {showLegalText && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-600 space-y-2">
                  <a 
                    href="https://teamsyncai.com/legal/privacy" 
                    className="block text-indigo-600 hover:text-indigo-700 underline"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  <a 
                    href="https://teamsyncai.com/legal/data-processing" 
                    className="block text-indigo-600 hover:text-indigo-700 underline"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Data Processing Agreement
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Start Button */}
        <div className="sticky bottom-0 bg-white/80 backdrop-blur-sm border-t border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="max-w-5xl mx-auto">
            <button
              onClick={handleStart}
              disabled={!canStart || loading}
              className={`w-full sm:w-auto sm:min-w-[280px] mx-auto flex items-center justify-center space-x-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                canStart && !loading
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                  <span>Starting Interview...</span>
                </>
              ) : (
                <>
                  <PlayIcon className="w-6 h-6" />
                  <span>Start Interview</span>
                </>
              )}
            </button>

            {!canStart && !loading && (
              <p className="text-center text-sm text-gray-500 mt-3">
                {!selectedFormat && 'Please select an answer format'}
                {selectedFormat && !termsAccepted && 'Please accept the terms & conditions'}
                {selectedFormat && termsAccepted && !recordingConsent && 'Please provide recording consent'}
                {selectedFormat && termsAccepted && recordingConsent && !mediaPermissionGranted && selectedFormat !== 'text' && mediaError && 'Device error - please fix or switch to Text mode'}
                {selectedFormat && termsAccepted && recordingConsent && !mediaPermissionGranted && selectedFormat !== 'text' && !mediaError && 'Please allow media access'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewStart;